import { Theme } from '@material-ui/core';

import { generateSemanticColorStyles } from '../utils';
import { linkClassName } from './Link';

export const tiptapBlockMarginTop = 'var(--bui-space-2)';
export const tiptapBlockMarginBottom =
  'var(--tiptap-block-margin-bottom, var(--bui-space-6))';
export const tiptapTextPadding = 'var(--bui-space-1) 0';

export const getEditorStyles = (theme: Theme) => ({
  position: 'relative' as const,
  maxWidth: '92ch',
  ...generateSemanticColorStyles(theme.palette.type === 'dark'),
  '& > strong': {
    fontWeight: 'var(--bui-font-weight-bold)',
  },
  '& > .ProseMirror': {
    outline: 'none',
  },
  '& .ProseMirror-dropcursor': {
    border: 'none',
  },
  '& .editor-paragraph': {
    fontFamily: 'var(--encore-body-font-stack)',
    fontSize: 'var(--bui-font-size-4)',
    // BUI has no line-height tokens; 1.5 is standard for readable body text
    lineHeight: 1.5,
    color: 'var(--bui-fg-primary)',
    padding: tiptapTextPadding,
    margin: 0,
    cursor: 'text',
  },
  '& .editor-paragraph.is-empty::before': {
    color: 'var(--bui-fg-disabled)',
    content: 'attr(data-placeholder)',
    float: 'left' as const,
    height: 0,
    pointerEvents: 'none' as const,
  },
  '& .caption': {
    fontFamily: 'var(--encore-body-font-stack)',
    fontSize: 'var(--bui-font-size-2)',
    lineHeight: 1.4,
    color: 'var(--bui-fg-secondary)',
    fontWeight: 700,
    letterSpacing: '0.5px',
    margin: 0,
    padding: tiptapTextPadding,
    cursor: 'text',
  },
  '& p.is-editor-empty::before': {
    color: 'var(--bui-fg-disabled)',
    content: 'attr(data-placeholder)',
    float: 'left' as const,
    height: 0,
    pointerEvents: 'none' as const,
  },
  [`& a.${linkClassName}`]: {
    color: 'var(--bui-fg-link)',
    textDecoration: 'underline',
    textDecorationThickness: '0.05em',
    textUnderlineOffset: '20%',
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--bui-fg-link-hover)',
    },
  },
  '& :not(pre) > code': {
    backgroundColor: 'var(--bui-bg-surface-1)',
    border: '1px solid var(--bui-border)',
    borderRadius: 'var(--bui-radius-2)',
    color: 'var(--bui-fg-primary)',
    fontFamily: 'var(--bui-font-monospace)',
    fontSize: 'var(--bui-font-size-2)',
    padding: '0 var(--bui-space-1)',
  },
  '& [data-type="callout-block"]': {
    marginTop: tiptapBlockMarginTop,
    marginBottom: tiptapBlockMarginBottom,
  },
  '& [data-type="callout-block"] .editor-paragraph': {
    padding: 0,
  },
  '& .editor-heading': {
    margin: 0,
    padding: tiptapTextPadding,
  },
  '& .editor-heading-4': {
    fontSize: 'var(--bui-font-size-4)',
    marginTop: 'var(--bui-space-3)',
  },
  '& .editor-heading-3': {
    fontSize: 'var(--bui-font-size-5)',
    marginTop: 'var(--bui-space-4)',
  },
  '& .editor-heading-2': {
    fontSize: 'var(--bui-font-size-6)',
    marginTop: 'var(--bui-space-6)',
  },
  '& .editor-heading-1': {
    fontSize: 'var(--bui-font-size-7)',
    marginTop: 'var(--bui-space-8)',
  },
  '& > .ProseMirror > .editor-heading:first-child': {
    marginTop: 0,
  },
  '& ul.editor-bullet-list, & ol.editor-ordered-list': {
    margin: 'var(--bui-space-2) 0',
    paddingLeft: 'var(--bui-space-1)',
    fontSize: 'var(--bui-font-size-3)',
  },
  '& ul.editor-bullet-list': {
    listStyleType: 'none',
    marginBottom: tiptapBlockMarginBottom,
    '& > li': {
      alignItems: 'baseline',
      display: 'flex',
      '&::before': {
        backgroundColor: 'var(--bui-bg-solid)',
        borderRadius: '50%',
        content: '""',
        height: 6,
        flexShrink: 0,
        marginRight: '0.5em',
        position: 'relative' as const,
        top: '-1px',
        width: 6,
      },
    },
    '& > li > p': {
      margin: 0,
      padding: 'var(--bui-space-1)',
    },
  },
  '& ol.editor-ordered-list': {
    paddingLeft: 'var(--bui-space-6)',
    '& > li > p': {
      margin: 0,
      padding: tiptapTextPadding,
    },
  },
  '& hr': {
    border: 'none',
    borderTop: '1px solid var(--bui-border)',
    margin: 'var(--bui-space-4) 0',
  },
  '& .editor-columns': {
    display: 'flex',
    margin: 0,
    marginBottom: tiptapBlockMarginBottom,
    marginTop: tiptapBlockMarginTop,
    width: '100%',
  },
  '& .editor-column': {
    flex: '1 1 0%',
    minWidth: 0,
    margin: 0,
    borderRight: '1px solid transparent',
    paddingRight: 'var(--bui-space-2)',
    paddingLeft: 'var(--bui-space-2)',
    transition: 'border-color 0.1s ease-in-out',
    '& .editor-paragraph': {
      position: 'relative' as const,
    },
    '& .editor-paragraph.is-empty::before': {
      float: 'none' as const,
      height: 'auto',
      position: 'absolute' as const,
      left: 0,
      right: 0,
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '&:first-child': {
      paddingLeft: 0,
    },
    '&:last-child': {
      borderRight: 'none',
      paddingRight: 0,
    },
    '& > :last-child': {
      '--tiptap-block-margin-bottom': tiptapBlockMarginTop,
    },
  },
  '& .editor-columns:hover .editor-column:not(:last-child)': {
    borderRight: '1px solid var(--bui-border)',
  },
  '& .editor-column--focused:not(:last-child)': {
    borderRight: '1px solid var(--bui-border)',

    '& > *:last-child': {
      marginBottom: 0,
    },
  },
  '& [data-type="callout-block"] ul.editor-bullet-list, & [data-type="callout-block"] ol.editor-ordered-list':
    {
      margin: 0,
      paddingLeft: 0,
    },
  '& table': {
    borderCollapse: 'separate' as const,
    borderSpacing: 0,
    tableLayout: 'fixed' as const,
    width: '100%',
    minWidth: '600px',
    marginBottom: tiptapBlockMarginBottom,
    marginTop: tiptapBlockMarginTop,
    lineHeight: 1.4,
    border: '1px solid var(--bui-border)',
    borderRadius: 'var(--bui-space-1)',

    '& td, & th': {
      minWidth: '100px',
      borderBottom:
        '1px solid color-mix(in srgb, var(--bui-border) 40%, transparent)',
      borderRight:
        '1px solid color-mix(in srgb, var(--bui-border) 40%, transparent)',
      padding: 'var(--bui-space-2) var(--bui-space-2)',
      verticalAlign: 'top',
      boxSizing: 'border-box' as const,
      position: 'relative' as const,
      color: 'var(--bui-fg-primary)',

      '&:last-child': {
        borderRight: 'none',
      },

      '& > *': {
        marginBottom: 0,
      },

      '& > p': {
        fontSize: 'var(--bui-font-size-3)',
        lineHeight: 1.4,
      },
    },

    '& tr:last-child td': {
      borderBottom: 'none',
    },

    '& th': {
      fontWeight: 'inherit',
      textAlign: 'left' as const,
      borderBottom: '1px solid var(--bui-border)',
    },

    '& .selectedCell:after': {
      zIndex: 2,
      position: 'absolute' as const,
      content: '""',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      backgroundColor: 'var(--bui-bg-tint-pressed)',
      pointerEvents: 'none' as const,
    },

    '& td .editor-paragraph.is-empty::before, & th .editor-paragraph.is-empty::before':
      {
        float: 'none' as const,
        height: 'auto',
        position: 'absolute' as const,
        left: 'var(--bui-space-2)',
        right: 'var(--bui-space-2)',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },

    '& .column-resize-handle': {
      position: 'absolute' as const,
      right: -8,
      top: 0,
      bottom: -2,
      width: 16,
      background: 'transparent',
      cursor: 'col-resize',

      '&::after': {
        content: '""',
        position: 'absolute' as const,
        left: 7,
        top: 0,
        bottom: 0,
        width: 2,
        backgroundColor: 'transparent',
        transition: 'background-color 0.15s',
      },

      '&:hover::after': {
        backgroundColor: 'var(--bui-border-pressed)',
      },
    },
  },
  '& .tiptap-comment-highlight': {
    backgroundColor: 'rgba(255, 223, 0, 0.3)',
    cursor: 'pointer',
    borderBottom: '2px solid var(--bui-fg-warning)',
    '&:hover': {
      backgroundColor: 'rgba(255, 223, 0, 0.5)',
    },
  },
});
